import React, { useCallback, useEffect, useRef } from "react";

import Badge, { BadgeVariant } from "../../../../../shared/components/design-system/Badge";
import Icon, { IconSlug } from "../../../../../shared/components/design-system/Icon";
import InputLabel from "../../../../../shared/components/design-system/InputLabel";
import { TextElement, Translatable } from "../../../../../shared/components/translation";
import useGeneratedId from "../../../../../shared/hooks/useGeneratedId";
import styles from "./styles.module.scss";

export interface CheckboxProps {
  label: Translatable;
  checked: boolean;
  onChange: (isSelected: boolean) => void;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  invalidReason?: string | null;
  badges?: Array<{ label: string; variant: BadgeVariant }>;
  className?: string;
  icon?: IconSlug;
  describedBy?: string;
}

export default function Checkbox({
  checked,
  onChange,
  isIndeterminate,
  isDisabled,
  invalidReason,
  badges,
  icon,
  label,
  className,
  describedBy,
}: CheckboxProps) {
  const id = useGeneratedId();
  const handleChange = useCallback(() => onChange(!checked), [onChange, checked]);

  // These properties have to be applied via Javascript rather than HTML attributes:
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current!.indeterminate = !!isIndeterminate;
  }, [isIndeterminate]);

  useEffect(() => {
    ref.current?.setCustomValidity(invalidReason ?? "");
  }, [invalidReason]);

  return (
    <div className={`${styles.container} ${className}`}>
      <input
        id={id}
        checked={checked}
        type="checkbox"
        onChange={handleChange}
        disabled={isDisabled}
        ref={ref}
        aria-describedby={describedBy}
      />
      <InputLabel variant="paragraph" htmlFor={id} className={styles.label}>
        <span className={styles.checkbox}>
          {isIndeterminate ? <Icon icon="minus" size={20} /> : checked ? <Icon icon="check" size={20} /> : null}
        </span>
        {icon ? <Icon icon={icon} inline /> : null}
        <TextElement tag="span" props={label} />
      </InputLabel>

      {badges ? badges.map((badge, i) => <Badge key={i} {...badge} className={styles.badge} />) : null}
    </div>
  );
}
